import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { apiErrorMessage } from '@/api/client'
import { settings as api } from '@/api/endpoints'
import { Alert, Checkbox, Field, Input, Spinner } from '@/components/ui'
import { PlanLock, usePlanFeature } from '@/features/billing/PlanFeature'
import { BankImports } from '@/features/payments/BankImports'
import { cn } from '@/lib/utils'
import type { Settings } from '@/types/api'

import { BankIcon, FIO, STATEMENT_BANKS, bankLabel } from './banks'

/**
 * Integrácie s bankami - Fio banka SK (API) a výpisy z ostatných slovenských
 * bánk (súbor). Zoznam bánk je v `banks.tsx`.
 *
 * Vlastná záložka, nie súčasť "Systém": moduly tam sú prepínače správania
 * appky, toto je pripojenie k cudziemu účtu a má vlastný pracovný postup
 * (uložiť token, potom opakovane spúšťať import), nie len jeden formulár na
 * uloženie.
 */
export default function IntegrationsTab() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['settings'], queryFn: api.get })

  const [form, setForm] = useState<Partial<Settings>>({})
  // Fio aj výpisy z bánk sú jedna funkcia balíčka - párovanie platieb.
  const banks = usePlanFeature('bank_import').allowed
  const [message, setMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    if (data) setForm(data)
  }, [data])

  const statementBanks = (form.statement_banks ?? '').split(',').filter(Boolean)

  function set<K extends keyof Settings>(key: K, value: Settings[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const save = useMutation({
    mutationFn: (payload: Partial<Settings>) => api.update(payload),
    onSuccess: () => {
      setMessage({ kind: 'success', text: t('global.saved') })
      void qc.invalidateQueries({ queryKey: ['settings'] })
    },
    onError: (err) => setMessage({ kind: 'error', text: apiErrorMessage(err) }),
  })

  if (isLoading || !data) {
    return (
      <div className="flex h-40 items-center justify-center text-slate-400">
        <Spinner className="h-5 w-5" />
      </div>
    )
  }

  return (
    <div className="max-w-3xl space-y-5">
      {message ? <Alert variant={message.kind}>{message.text}</Alert> : null}

      <section className="card space-y-2 p-4">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <BankIcon bank={FIO} size={24} />
          {bankLabel(FIO)}
        </h2>
        <Checkbox
          label={t('settings.moduleFio')}
          checked={form.module_fio ?? false}
          disabled={!banks && !form.module_fio}
          onChange={(e) => set('module_fio', e.target.checked)}
        />
        <PlanLock feature="bank_import" />

        {/* Token sa nikdy nevracia z API - prázdne pole preto neznamená
            "vymazať", ale "nechať tak"; že je nastavený, povie stav pod ním. */}
        {form.module_fio ? (
          <div className="mt-2 space-y-2 rounded-md border border-slate-200 p-3">
            <p className="text-xs text-slate-500">{t('settings.fioHint')}</p>
            <Field label={t('settings.fioToken')} className="sm:max-w-xs">
              <Input
                type="password"
                autoComplete="off"
                placeholder={data.modules.fio.configured ? '••••••••' : ''}
                value={form.fio_token ?? ''}
                onChange={(e) => set('fio_token', e.target.value)}
              />
            </Field>
            <p
              className={
                data.modules.fio.configured ? 'text-xs text-emerald-700' : 'text-xs text-amber-700'
              }
            >
              {data.modules.fio.configured
                ? t('settings.fioReady')
                : t('settings.fioMissingToken')}
            </p>
          </div>
        ) : null}

        <div>
          <button
            type="button"
            className="btn-primary"
            disabled={save.isPending}
            onClick={() => {
              setMessage(null)
              // Len polia tejto sekcie - celý formulár by spadol na chybe
              // v nesúvisiacom nastavení (napr. neplatný IBAN QR platby).
              save.mutate({ module_fio: form.module_fio, fio_token: form.fio_token })
            }}
          >
            {save.isPending ? <Spinner /> : null}
            {t('global.save')}
          </button>
        </div>
      </section>

      {/* Výpisy z bánk - jeden spoločný import (camt.053 / OFX), banka určuje
          názov, pôvod platby a kontrolu, či výpis patrí jej. */}
      <section className="card space-y-3 p-4">
        <div>
          <h2 className="text-sm font-semibold">{t('settings.statementTitle')}</h2>
          <p className="text-xs text-slate-500">{t('settings.statementIntro')}</p>
          <PlanLock feature="bank_import" />
        </div>
        <ul className="grid gap-2 sm:grid-cols-2">
          {STATEMENT_BANKS.map((bank) => {
            const enabled = statementBanks.includes(bank.code)
            return (
              <li key={bank.code}>
                <label
                  className={cn(
                    'flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2',
                    enabled ? 'border-brand-300 bg-brand-50/40' : 'border-slate-200',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={enabled}
                    disabled={!banks && !enabled}
                    onChange={(e) =>
                      set(
                        'statement_banks',
                        (e.target.checked
                          ? [...statementBanks, bank.code]
                          : statementBanks.filter((code) => code !== bank.code)
                        ).join(','),
                      )
                    }
                  />
                  <BankIcon bank={bank} />
                  <span className="text-sm font-medium">{bankLabel(bank)}</span>
                </label>
              </li>
            )
          })}
        </ul>
        <div>
          <button
            type="button"
            className="btn-primary"
            disabled={save.isPending}
            onClick={() => {
              setMessage(null)
              save.mutate({ statement_banks: form.statement_banks ?? '' })
            }}
          >
            {save.isPending ? <Spinner /> : null}
            {t('global.save')}
          </button>
        </div>
      </section>

      {/* Samotný import - ten istý je aj v Platby → Import z banky, kde ho
          spúšťa aj manažér rezervácií. Tu sa banky nastavujú. */}
      <BankImports settings={data} onError={(text) => setMessage({ kind: 'error', text })} />
    </div>
  )
}
